/**
 * Welcome Guide - First-time tooltip tour for new users
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Sparkles, Tags, QrCode, BarChart3, Settings } from 'lucide-react';

const GUIDE_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Your Dashboard!',
    description: 'Let me show you around. This quick tour will help you get started with managing your NFC tags.',
    icon: Sparkles,
    position: 'center'
  },
  {
    id: 'quick-create',
    title: 'Quick Create',
    description: 'Click here to instantly create a new tag. The upload link is automatically copied to your clipboard!',
    icon: Tags,
    target: '[data-testid="quick-create-btn"]',
    position: 'bottom'
  },
  {
    id: 'tabs',
    title: 'Navigation Tabs',
    description: 'Switch between All Tags, Batches, UUID Generator, Custom Tag creation, and Bulk Upload.',
    icon: Tags,
    target: '[data-testid="tags-tab"]',
    position: 'bottom'
  },
  {
    id: 'tag-card',
    title: 'Tag Management',
    description: 'Click the menu (⋮) on any tag to view analytics, download QR codes, copy URLs, set dates, or delete.',
    icon: QrCode,
    target: '[data-testid^="menu-btn-"]',
    position: 'left'
  },
  {
    id: 'search',
    title: 'Search & Filter',
    description: 'Use the search bar and filters to quickly find tags by name, status, or batch.',
    icon: Tags,
    target: '[data-testid="search-input"]',
    position: 'bottom'
  },
  {
    id: 'done',
    title: 'You\'re All Set!',
    description: 'You now know the basics. Explore the dashboard and start creating amazing NFC experiences!',
    icon: Sparkles,
    position: 'center'
  }
];

const STORAGE_KEY = 'nfc_welcome_guide_completed';

export const WelcomeGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Check if user has completed the guide
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      // Small delay to let the dashboard render first
      setTimeout(() => setIsOpen(true), 1000);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    
    const step = GUIDE_STEPS[currentStep];
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        // Scroll element into view if needed
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep, isOpen]);

  const handleNext = () => {
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  const step = GUIDE_STEPS[currentStep];
  const Icon = step.icon;
  const isCenter = step.position === 'center';

  // Calculate tooltip position based on target
  const getTooltipStyle = () => {
    // On mobile, always center the tooltip
    if (isMobile || !targetRect || isCenter) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '384px',
        width: isMobile ? 'calc(100vw - 32px)' : 'auto'
      };
    }

    const padding = 16;
    const tooltipWidth = 320; // approximate tooltip width
    
    // Calculate position and clamp to screen bounds
    let style = {};
    
    switch (step.position) {
      case 'bottom':
        style = {
          top: Math.min(targetRect.bottom + padding, window.innerHeight - 300),
          left: Math.max(padding, Math.min(targetRect.left + targetRect.width / 2, window.innerWidth - tooltipWidth / 2 - padding)),
          transform: 'translateX(-50%)'
        };
        break;
      case 'top':
        style = {
          bottom: Math.max(padding, window.innerHeight - targetRect.top + padding),
          left: Math.max(padding, Math.min(targetRect.left + targetRect.width / 2, window.innerWidth - tooltipWidth / 2 - padding)),
          transform: 'translateX(-50%)'
        };
        break;
      case 'left':
        style = {
          top: Math.max(padding, Math.min(targetRect.top + targetRect.height / 2, window.innerHeight - 200)),
          right: Math.max(padding, window.innerWidth - targetRect.left + padding),
          transform: 'translateY(-50%)'
        };
        break;
      case 'right':
        style = {
          top: Math.max(padding, Math.min(targetRect.top + targetRect.height / 2, window.innerHeight - 200)),
          left: Math.min(targetRect.right + padding, window.innerWidth - tooltipWidth - padding),
          transform: 'translateY(-50%)'
        };
        break;
      default:
        style = {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
    }
    
    return style;
  };

  return (
    <AnimatePresence>
      {/* Overlay - Very light on mobile, slightly darker on desktop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] pointer-events-none"
        style={{ backgroundColor: isMobile ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.25)' }}
      >
        {/* Highlight target element - only on desktop */}
        {!isMobile && targetRect && !isCenter && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute bg-white/20 border-2 border-primary rounded-xl pointer-events-none"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.25)'
            }}
          />
        )}

        {/* Tooltip - Always centered on mobile via CSS, positioned on desktop via JS */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="fixed bg-background rounded-2xl shadow-2xl border border-border p-4 sm:p-6 w-[calc(100vw-32px)] sm:w-auto sm:max-w-sm z-[101] inset-x-4 top-1/2 -translate-y-1/2 sm:inset-auto pointer-events-auto"
          style={!isMobile ? getTooltipStyle() : undefined}
        >
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Icon */}
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>

          {/* Content */}
          <h3 className="text-base sm:text-lg font-bold mb-2 pr-6">{step.title}</h3>
          <p className="text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6">{step.description}</p>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-3 sm:mb-4">
            {GUIDE_STEPS.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-primary' : 'bg-secondary'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={handleSkip}
              className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
            </button>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-xs sm:text-sm font-medium transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-3 sm:px-4 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-xs sm:text-sm font-medium transition-colors"
              >
                {currentStep === GUIDE_STEPS.length - 1 ? 'Get Started' : 'Next'}
                {currentStep < GUIDE_STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Button to restart the guide
export const RestartGuideButton = () => {
  const handleRestart = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <button
      onClick={handleRestart}
      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
    >
      Restart Guide
    </button>
  );
};

export default WelcomeGuide;
